import { useEffect, useRef } from 'react';
import io, { Socket } from 'socket.io-client';
import { api } from '@/utils/api';
import type { GeoPoint } from '@/utils/geo';
import type { OnMyWayJob } from '@/hooks/useAutoMarkReached';

const backendUrl = import.meta.env.VITE_BACKEND_URL;

type RoomTarget = {
  room: string;
  jobId: string;
};

type UseWorkerLocationSocketOptions = {
  jobs: OnMyWayJob[];
  workerId: string;
  workerPosition: GeoPoint | null;
  enabled?: boolean;
};

/**
 * Broadcasts worker GPS to client map rooms while on_my_way.
 * Uses the same workerPosition stream as useAutoMarkReached (no extra GPS watch).
 */
export function useWorkerLocationSocket({
  jobs,
  workerId,
  workerPosition,
  enabled = true,
}: UseWorkerLocationSocketOptions) {
  const positionRef = useRef(workerPosition);
  positionRef.current = workerPosition;

  const savedJobIdsRef = useRef(new Set<string>());
  const initialSentRoomsRef = useRef(new Set<string>());

  const activeRooms: RoomTarget[] = jobs
    .filter((j) => j.status === 'on_my_way' && j.job?.user?._id && j.job?._id)
    .map((j) => ({
      room: `${j.job!.user!._id}-${workerId}`,
      jobId: j.job!._id as string,
    }));

  const roomsKey = activeRooms.map((r) => r.room).join(',');

  useEffect(() => {
    if (!enabled || !workerId || activeRooms.length === 0) return;

    const socket: Socket = io(backendUrl || 'http://localhost:4000', {
      transports: ['websocket'],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    const broadcast = () => {
      const pos = positionRef.current;
      if (!pos) return;

      const location = { lat: pos.lat, lng: pos.lng };

      activeRooms.forEach(({ room, jobId }) => {
        socket.emit('getProfessionalLocation', { room, location });

        if (!initialSentRoomsRef.current.has(room)) {
          socket.emit('initialWorkerLocation', { room, location });
          initialSentRoomsRef.current.add(room);
        }

        if (!savedJobIdsRef.current.has(jobId)) {
          savedJobIdsRef.current.add(jobId);
          api
            .saveWorkerInitialLocation({
              jobId,
              workerId,
              location: { latitude: pos.lat, longitude: pos.lng },
            })
            .catch((err) => console.error('Failed to save worker location', err));
        }
      });
    };

    const handleConnect = () => {
      activeRooms.forEach(({ room }) => socket.emit('joinRoom', room));
      broadcast();
    };

    socket.on('connect', handleConnect);
    socket.connect();

    const interval = setInterval(broadcast, 5000);

    return () => {
      clearInterval(interval);
      socket.off('connect', handleConnect);
      activeRooms.forEach(({ room }) => socket.emit('leaveRoom', room));
      socket.disconnect();
    };
  }, [enabled, workerId, roomsKey]);

  return { isBroadcasting: enabled && activeRooms.length > 0 };
}
